import { environment } from '@environments/environment';

export type FacingMode = 'user' | 'environment';

export abstract class WebrtcBaseService {
  protected readonly AUDIO_BITRATE: number = 128;
  protected readonly START_VIDEO_BITRATE: number = 1000;
  protected readonly VIDEO_BITRATE: number = 5000;
  protected readonly MAX_VIDEO_BITRATE: number = 6000;

  protected async createOffer(
    peerConnection: RTCPeerConnection,
    needSdpModifyAudio: boolean,
    needSdpModifyVideo: boolean,
    needReceiveAudio: boolean,
    needReceiveVideo: boolean,
    iceRestart: boolean = false
  ): Promise<RTCSessionDescriptionInit> {
    try {
      const offerOptions: RTCOfferOptions = {
        offerToReceiveAudio: needReceiveAudio,
        offerToReceiveVideo: needReceiveVideo,
        iceRestart,
      };
      const offer: RTCSessionDescriptionInit = await peerConnection.createOffer(offerOptions);
      if (environment.highMediaQuality) {
        if (needSdpModifyVideo) {
          offer.sdp = this.modifySDP(offer.sdp, 'H264', this.START_VIDEO_BITRATE, this.MAX_VIDEO_BITRATE);
        }
        if(needSdpModifyAudio){
          offer.sdp = this.prioritizeCodec(offer.sdp, 'opus');
          offer.sdp = this.updateBandwidthRestriction(offer.sdp, this.AUDIO_BITRATE);
        }

      }
      return offer;
    } catch (error) {
      console.error('Error creating offer:', error);
      throw error;
    }
  }

  protected isCodecSupported(sdp: string, codecName: string): boolean {
    try {
      const codecRegex = new RegExp(`a=rtpmap:\\d+ ${codecName}/\\d+`, 'i');
      return codecRegex.test(sdp);
    } catch (error) {
      console.error('Error checking codec support:', error);
      return false;
    }
  }

  protected prioritizeCodec(sdp: string, codec: string): string {
    const supports: boolean = this.isCodecSupported(sdp, codec);

    if (supports) {
      const codecRegex = new RegExp(`a=rtpmap:(\\d+) ${codec}/\\d+`, 'i');
      const match: RegExpMatchArray = sdp.match(codecRegex);

      if (match) {
        const [_line, payloadType] = match;
        const mediaRegex = /m=video.*\r\n/;

        sdp = sdp.replace(mediaRegex, (mediaLine: string): string => {
          const elements: string[] = mediaLine.split(' ');
          const payload: string = elements.find((el: string): boolean => el === payloadType);

          if (payload) {
            elements.splice(elements.indexOf(payload), 1);
            elements.splice(3, 0, payload); // move the codec to the beginning
          }
          return elements.join(' ');
        });
      }
      console.debug(`Set ${codec} codec priority.`);
    } else {
      console.warn(`${codec} codec is not supported!`);
    }
    return sdp;
  }

  updateBandwidthRestriction(sdp: string, bandwidth: number) {
    let modifier = 'AS';
    // if (adapter.browserDetails.browser === 'firefox') {
    //   bandwidth = (bandwidth >>> 0) * 1000;
    //   modifier = 'TIAS';
    // }
    if (sdp.indexOf('b=' + modifier + ':') === -1) {
      // insert b= after c= line.
      sdp = sdp.replace(/c=IN (.*)\r\n/, 'c=IN $1\r\nb=' + modifier + ':' + bandwidth + '\r\n');
    } else {
      sdp = sdp.replace(new RegExp('b=' + modifier + ':.*\r\n'), 'b=' + modifier + ':' + bandwidth + '\r\n');
    }
    return sdp;
  }

  protected modifyBitrate(sdp: string, startVideoBitrate: number, maxVideoBitrate: number,): string {
    const lines: string[] = sdp.split('\r\n');
    const modifiedLines: string[] = [];

    lines.forEach((line): void => {
      if (/^a=fmtp:\d+/.test(line)) {
        modifiedLines.push(`${line};x-google-start-bitrate=${startVideoBitrate};x-google-max-bitrate=${maxVideoBitrate}`);
        // modifiedLines.push(`${line};x-google-max-bitrate=${maxVideoBitrate}`);
      } else if (/^m=video/.test(line)) {
        modifiedLines.push(line);
        modifiedLines.push(`b=AS:${maxVideoBitrate}`);
      } else {
        modifiedLines.push(line);
      }
    });
    console.debug(`Modify sdp: startBitrate=${startVideoBitrate}, maxBitrate=${maxVideoBitrate}`);

    return modifiedLines.join('\r\n');
  }

  protected modifySDP(sdp: string, codec: string, startVideoBitrate: number, maxVideoBitrate: number): string {
    sdp = this.prioritizeCodec(sdp, codec);
    sdp = this.modifyBitrate(sdp, startVideoBitrate, maxVideoBitrate);
    sdp = this.updateBandwidthRestriction(sdp, maxVideoBitrate);
    return sdp;
  }

  protected getCodecPreferencesWithRtx(capabilities: RTCRtpCapabilities | null, preferredMime: string = 'video/h264'): RTCRtpCodecCapability[] {
    if (!capabilities?.codecs?.length) {
      return [];
    }

    const codecs: RTCRtpCodecCapability[] = capabilities.codecs;
    const prioritizedMime: string = preferredMime.toLowerCase();
    const ordered: RTCRtpCodecCapability[] = [];
    const added = new Set<RTCRtpCodecCapability>();

    const pushCodecWithRtx = (codec: RTCRtpCodecCapability): void => {
      if (added.has(codec)) {
        return;
      }

      ordered.push(codec);
      added.add(codec);

      const payloadType: number | null = this.getCodecPayloadType(codec);
      if (payloadType === null) {
        return;
      }

      codecs.forEach((candidate: RTCRtpCodecCapability): void => {
        if (added.has(candidate)) {
          return;
        }

        if (candidate.mimeType?.toLowerCase() === 'video/rtx') {
          const apt: number | null = this.extractAptFromFmtp(candidate.sdpFmtpLine);
          if (apt === payloadType) {
            ordered.push(candidate);
            added.add(candidate);
          }
        }
      });
    };

    codecs.forEach((codec: RTCRtpCodecCapability): void => {
      if (codec.mimeType?.toLowerCase() === prioritizedMime) {
        pushCodecWithRtx(codec);
      }
    });

    codecs.forEach((codec: RTCRtpCodecCapability): void => {
      if (!added.has(codec)) {
        pushCodecWithRtx(codec);
      }
    });

    return ordered;
  }

  private extractAptFromFmtp(fmtpLine?: string): number | null {
    if (!fmtpLine) {
      return null;
    }

    const match: RegExpMatchArray | null = fmtpLine.match(/apt=(\d+)/i);
    if (!match) {
      return null;
    }

    return Number.parseInt(match[1], 10);
  }

  private getCodecPayloadType(codec: RTCRtpCodecCapability): number | null {
    const preferredPayloadType: unknown = Reflect.get(codec, 'preferredPayloadType');
    if (typeof preferredPayloadType === 'number') {
      return preferredPayloadType;
    }

    const payloadType: unknown = Reflect.get(codec, 'payloadType');
    if (typeof payloadType === 'number') {
      return payloadType;
    }

    return null;
  }

  protected async setVideoQuality(sender: RTCRtpSender, maxBitrateKbps: number): Promise<void> {
    console.debug('setVideoQuality');
    const patchParam = async (paramName: string, paramValue: any): Promise<void> => {
      try {
        const params: RTCRtpSendParameters = sender.getParameters();

        if (!params?.encodings?.length) {
          params.encodings = [{}];
        }

        if (paramName === 'degradationPreference') {
          params.degradationPreference = paramValue;
        } else {
          params.encodings[0][paramName] = paramValue;
        }

        await sender.setParameters(params);
        console.debug(`[video] - ${paramName} set to`, paramValue);
      } catch (error) {
        console.error(`Error setting ${paramName}:`, error);
      }
    };

    await patchParam('maxBitrate', maxBitrateKbps * 1000);
    await patchParam('scaleResolutionDownBy', 1);
    await patchParam('degradationPreference', 'maintain-framerate');
    await patchParam('priority', 'high');
    await patchParam('networkPriority', 'high'); // experimental
    await patchParam('adaptivePtime', true); // experimental

    console.debug('Final parameters:', sender.getParameters());
  }

  protected async setAudioQuality(sender: RTCRtpSender, maxBitrateKbps: number): Promise<void> {
    console.debug('setAudioQuality');
    try {
      const params: RTCRtpSendParameters = sender.getParameters();

      if (!params?.encodings?.length) {
        params.encodings = [{}];
      }
      params.encodings[0].maxBitrate = maxBitrateKbps * 1000;

      await sender.setParameters(params);
      console.debug(`[audio] - maxBitrate set to`, params.encodings[0].maxBitrate);
    } catch (error) {
      console.error('Error setting audio quality:', error);
    }
  }

  /* protected async onNegotiationNeeded(event: Event, peerConnection: RTCPeerConnection): Promise<void> {
    console.log('Negotiation needed, preparing to set video quality...');
    try {
      setTimeout(async () => {
        const videoSender: RTCRtpSender = peerConnection?.getSenders().find((sender: RTCRtpSender) => sender.track?.kind === 'video');

        if (videoSender && environment.highMediaQuality) {
          await this.setVideoQuality(videoSender, this.VIDEO_BITRATE);
          console.log('Video quality set to 5 Mbps after negotiation');
        }
      }, 2000);
    } catch (error) {
      console.error('Error setting video quality after negotiation:', error);
    }
  } */
}
